import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getTicket, addTicketMessage, updateTicketStatus, updateTicket,
  uploadTicketAttachment, getTicketAssignees, getTicketCategories
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Send, Clock, CheckCircle2, AlertCircle,
  MessageCircle, Shield, Paperclip, Download, AlertTriangle, Timer,
  UserCircle, FileText as FileIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { logError } from '../utils/logger';
import { useRealtime } from '../contexts/RealtimeContext';

const STATUS_CONFIG = {
  aberto: { label: 'Aberto', className: 'bg-red-50 text-red-700 border-red-200', icon: AlertCircle },
  em_andamento: { label: 'Em Andamento', className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  resolvido: { label: 'Resolvido', className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
};
const PRIORITY_CONFIG = {
  alta: { label: 'Alta', className: 'bg-red-100 text-red-700 border-red-200' },
  media: { label: 'Média', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  baixa: { label: 'Baixa', className: 'bg-blue-100 text-blue-700 border-blue-200' },
};

const isSlaBreached = (t) => {
  if (!t?.sla_deadline || t.status === 'resolvido') return false;
  try { return new Date(t.sla_deadline) < new Date(); } catch { return false; }
};

const formatBytes = (n) => {
  if (!n) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const isImage = (filename) => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename || '');

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { subscribe, subscribeTicket, unsubscribeTicket } = useRealtime();
  const isAdmin = user?.role === 'admin';
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [categories, setCategories] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchTicket = async (silent = false) => {
    try {
      const res = await getTicket(id);
      setTicket(res.data);
    } catch (e) {
      if (!silent) {
        toast.error('Chamado não encontrado');
        navigate('/tickets');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getTicket(id);
        if (mounted) {
          setTicket(res.data);
          setLoading(false);
        }
      } catch (e) {
        if (mounted) {
          toast.error('Chamado não encontrado');
          navigate('/tickets');
        }
      }
    };
    load();
    // Polling fallback raro (60s) caso WebSocket esteja desconectado
    const interval = setInterval(async () => {
      try {
        const res = await getTicket(id);
        if (mounted) setTicket(res.data);
      } catch (e) { logError("TicketDetail", e); }
    }, 60000);
    return () => { mounted = false; clearInterval(interval); };
  }, [id]);

  // Subscribe to WS events para o detail deste ticket
  useEffect(() => {
    if (!id) return undefined;
    subscribeTicket(id);

    const unsubMsg = subscribe('ticket_message_created', (msg) => {
      if (msg.ticket_id !== id) return;
      setTicket((prev) => {
        if (!prev) return prev;
        // Evita duplicar se a mensagem já está na lista
        const exists = prev.messages.some(m => m.id === msg.message.id);
        if (exists) return prev;
        return {
          ...prev,
          messages: [...prev.messages, msg.message],
          message_count: (prev.message_count || 0) + 1,
          updated_at: msg.message.created_at,
        };
      });
    });

    const unsubStatus = subscribe('ticket_status_changed', (msg) => {
      if (msg.ticket_id !== id) return;
      setTicket((prev) => prev ? { ...prev, status: msg.status } : prev);
    });

    const unsubUpdated = subscribe('ticket_updated', async (msg) => {
      if (msg.ticket_id !== id) return;
      // Recarrega para pegar assigned_to_name resolvido
      try {
        const res = await getTicket(id);
        setTicket(res.data);
      } catch (e) { logError('TicketDetail.wsUpdate', e); }
    });

    return () => {
      unsubMsg();
      unsubStatus();
      unsubUpdated();
      unsubscribeTicket(id);
    };
  }, [id, subscribe, subscribeTicket, unsubscribeTicket]);

  useEffect(() => {
    let mounted = true;
    const loadMeta = async () => {
      try {
        const cats = await getTicketCategories();
        if (mounted) setCategories(cats.data);
      } catch (e) { logError("TicketDetail", e); }
      if (isAdmin) {
        try {
          const ass = await getTicketAssignees();
          if (mounted) setAssignees(ass.data);
        } catch (e) { logError("TicketDetail", e); }
      }
    };
    loadMeta();
    return () => { mounted = false; };
  }, [isAdmin]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages?.length]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      await addTicketMessage(id, { message: message.trim() });
      setMessage('');
      fetchTicket(true);
    } catch (e) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateTicketStatus(id, newStatus);
      toast.success('Status atualizado');
      fetchTicket(true);
    } catch (e) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleAssign = async (assigneeId) => {
    try {
      await updateTicket(id, { assigned_to: assigneeId || null });
      toast.success(assigneeId ? 'Chamado atribuído' : 'Atribuição removida');
      fetchTicket(true);
    } catch (e) {
      toast.error('Erro ao atribuir');
    }
  };

  const handleCategoryChange = async (category) => {
    try {
      await updateTicket(id, { category });
      toast.success('Categoria atualizada');
      fetchTicket(true);
    } catch (e) {
      toast.error('Erro ao atualizar categoria');
    }
  };

  const handlePriorityChange = async (priority) => {
    try {
      await updateTicket(id, { priority });
      toast.success('Prioridade atualizada (SLA recalculado)');
      fetchTicket(true);
    } catch (e) {
      toast.error('Erro ao atualizar prioridade');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo maior que 10 MB');
      return;
    }
    setUploading(true);
    try {
      await uploadTicketAttachment(id, file);
      toast.success('Anexo enviado');
      e.target.value = '';
      fetchTicket(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar anexo');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return d; }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!ticket) return null;

  const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.aberto;
  const priorityCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.media;
  const slaBreached = isSlaBreached(ticket);
  const apiBase = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/tickets')} className="text-gray-500 hover:text-gray-700 mt-0.5" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-gray-900 break-words">{ticket.title}</h1>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              <Badge variant="outline" className={statusCfg.className}>{statusCfg.label}</Badge>
              <Badge variant="outline" className={priorityCfg.className}>Prioridade: {priorityCfg.label}</Badge>
              {ticket.category && (
                <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                  {(categories.find(c => c.value === ticket.category)?.label) || ticket.category}
                </Badge>
              )}
              {slaBreached && (
                <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 flex items-center gap-1" data-testid="sla-breached-badge">
                  <AlertTriangle className="w-3 h-3" />SLA Estourado
                </Badge>
              )}
              <span className="text-xs text-gray-500">Aberto por {ticket.created_by_name} em {formatDate(ticket.created_at)}</span>
            </div>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
              {ticket.assigned_to_name ? (
                <span className="flex items-center gap-1"><UserCircle className="w-3.5 h-3.5" />Atribuído a <strong className="text-gray-700">{ticket.assigned_to_name}</strong></span>
              ) : (
                <span className="text-gray-400 italic">Não atribuído</span>
              )}
              {ticket.sla_deadline && ticket.status !== 'resolvido' && (
                <span className="flex items-center gap-1"><Timer className="w-3.5 h-3.5" />Prazo SLA: {formatDate(ticket.sla_deadline)}</span>
              )}
              {ticket.first_response_at && (
                <span className="text-emerald-600">1ª resposta em {ticket.response_time_minutes != null ? `${ticket.response_time_minutes}min` : '—'}</span>
              )}
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-gray-500 uppercase tracking-wide">Status</Label>
              <Select value={ticket.status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-[170px] bg-white border-gray-300 text-gray-700" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="resolvido">Resolvido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-gray-500 uppercase tracking-wide">Atribuir</Label>
              <Select value={ticket.assigned_to || 'unassigned'} onValueChange={(v) => handleAssign(v === 'unassigned' ? null : v)}>
                <SelectTrigger className="w-[180px] bg-white border-gray-300 text-gray-700" data-testid="assign-select">
                  <SelectValue placeholder="Atribuir a..." />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="unassigned">Não atribuído</SelectItem>
                  {assignees.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-gray-500 uppercase tracking-wide">Categoria</Label>
              <Select value={ticket.category || 'duvida'} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-[170px] bg-white border-gray-300 text-gray-700" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {categories.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-[10px] text-gray-500 uppercase tracking-wide">Prioridade</Label>
              <Select value={ticket.priority || 'media'} onValueChange={handlePriorityChange}>
                <SelectTrigger className="w-[140px] bg-white border-gray-300 text-gray-700" data-testid="priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="media">Média</SelectItem>
                  <SelectItem value="baixa">Baixa</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Description */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-5">
          <p className="text-sm font-semibold text-gray-500 mb-2">Descrição do Chamado</p>
          <p className="text-gray-800 whitespace-pre-wrap">{ticket.description}</p>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card className="bg-white border-gray-200 shadow-md">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-[#003366]" />
          <h2 className="text-lg font-semibold text-gray-900">Mensagens</h2>
          <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200 ml-auto">{ticket.messages.length}</Badge>
        </div>
        <CardContent className="p-5">
          {ticket.messages.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Nenhuma mensagem ainda</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {ticket.messages.map((msg) => {
                const isMine = msg.user_id === user?.id;
                const isAdminMsg = msg.user_role === 'admin';
                const hasAttachments = msg.attachments && msg.attachments.length > 0;
                return (
                  <div key={msg.id} className={`flex gap-3 ${isMine ? 'flex-row-reverse' : ''}`} data-testid={`message-${msg.id}`}>
                    <Avatar className="h-8 w-8 flex-shrink-0">
                      <AvatarFallback className={`text-xs font-semibold ${isAdminMsg ? 'bg-[#003366] text-white' : 'bg-gray-200 text-gray-600'}`}>
                        {getInitials(msg.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={`max-w-[75%] ${isMine ? 'items-end' : 'items-start'}`}>
                      <div className={`rounded-xl px-4 py-2.5 ${isMine ? 'bg-[#003366] text-white' : 'bg-gray-100 text-gray-800'}`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                        {hasAttachments && (
                          <div className="mt-2 space-y-2">
                            {msg.attachments.map((att, idx) => (
                              <div key={idx} data-testid={`attachment-${msg.id}-${idx}`}>
                                {isImage(att.file_name) ? (
                                  <a
                                    href={`${apiBase}${att.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block"
                                  >
                                    <img
                                      src={`${apiBase}${att.file_url}`}
                                      alt={att.file_name}
                                      className="max-h-40 rounded-md border border-white/20"
                                    />
                                    <span className={`text-[10px] mt-1 block ${isMine ? 'text-white/70' : 'text-gray-500'}`}>
                                      {att.file_name} · {formatBytes(att.size_bytes)} · {formatDate(msg.created_at)}
                                    </span>
                                  </a>
                                ) : (
                                  <a
                                    href={`${apiBase}${att.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={att.file_name}
                                    className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs ${
                                      isMine ? 'bg-white/15 hover:bg-white/25' : 'bg-white hover:bg-gray-50 border border-gray-200'
                                    }`}
                                  >
                                    <FileIcon className="w-4 h-4" />
                                    <span className="font-medium truncate flex-1">{att.file_name}</span>
                                    <span className={isMine ? 'text-white/70' : 'text-gray-500'}>{formatBytes(att.size_bytes)} · {formatDate(msg.created_at)}</span>
                                    <Download className="w-3.5 h-3.5" />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className={`flex items-center gap-2 mt-1 ${isMine ? 'justify-end' : ''}`}>
                        <span className="text-xs text-gray-400">{msg.user_name}</span>
                        {isAdminMsg && <Shield className="w-3 h-3 text-[#003366]" />}
                        <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Message Input */}
          {(ticket.status !== 'resolvido' || isAdmin) && (
            <div className="flex gap-3 mt-5 pt-4 border-t border-gray-100">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={ticket.status === 'resolvido' ? 'Adicionar nota final...' : 'Digite sua mensagem...'}
                rows={2}
                className="bg-white border-gray-300 text-gray-900 resize-none flex-1"
                data-testid="message-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
              />
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
                data-testid="file-input"
                accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="bg-white border-gray-300 self-end h-10 w-10 p-0"
                data-testid="attach-btn"
                title="Anexar arquivo (max 10 MB)"
              >
                {uploading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#003366]" />
                ) : (
                  <Paperclip className="w-4 h-4 text-gray-600" />
                )}
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={sending || !message.trim()}
                className="bg-[#003366] hover:bg-[#002244] self-end h-10 w-10 p-0"
                data-testid="send-message-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
          {ticket.status === 'resolvido' && !isAdmin && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-100 text-emerald-600">
              <CheckCircle2 className="w-4 h-4" />
              <p className="text-sm font-medium">Este chamado foi resolvido</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TicketDetail;
